import Box from '@cloudscape-design/components/box'
import Cards from '@cloudscape-design/components/cards'
import Header from '@cloudscape-design/components/header'
import Pagination from '@cloudscape-design/components/pagination'
import SpaceBetween from '@cloudscape-design/components/space-between'
import Table from '@cloudscape-design/components/table'
import { useCollection } from '@cloudscape-design/collection-hooks'
import { useAgentDecisions } from '@/hooks/useAgentDecisions'
import type { AgentDecision } from '@/hooks/useAgentDecisions'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { formatDate } from '@/utils/format'

interface Props {
  clientId: string
}

export function RecentDecisionsSection({ clientId }: Props) {
  const { decisions, loading } = useAgentDecisions(clientId, 20)
  const isMobile = useMediaQuery('(max-width: 1023px)')

  const { items, collectionProps, paginationProps } = useCollection<AgentDecision>(decisions, {
    sorting: {},
    pagination: { pageSize: 10 },
  })

  if (isMobile) {
    return (
      <SpaceBetween size="m">
        <Cards<AgentDecision>
          {...collectionProps}
          items={items}
          loading={loading}
          loadingText="Loading decisions..."
          cardsPerRow={[{ cards: 1 }]}
          header={
            <Header variant="h2" description="What your agent decided and why">
              Recent Decisions
            </Header>
          }
          cardDefinition={{
            header: (item) => (
              <Box variant="strong">{formatDate(item.createdAt)}</Box>
            ),
            sections: [
              {
                id: 'reasoning',
                header: 'Reasoning',
                content: (item) => (
                  <Box>{item.agentReasoning}</Box>
                ),
              },
              {
                id: 'actions',
                header: 'Actions',
                content: (item) => (
                  <Box>
                    {item.validatedActions.map((a) => a.type).join(', ') || '—'}
                  </Box>
                ),
              },
            ],
          }}
          empty={
            <Box color="text-body-secondary" textAlign="center" padding="xl">
              No decisions yet
            </Box>
          }
        />
        <Pagination {...paginationProps} />
      </SpaceBetween>
    )
  }

  return (
    <Table<AgentDecision>
      {...collectionProps}
      loading={loading}
      loadingText="Loading decisions..."
      header={
        <Header variant="h2" description="What your agent decided and why">
          Recent Decisions
        </Header>
      }
      empty={
        <Box color="text-body-secondary" textAlign="center" padding="xl">
          No decisions yet
        </Box>
      }
      pagination={<Pagination {...paginationProps} />}
      columnDefinitions={[
        {
          id: 'date',
          header: 'Date',
          cell: (item) => formatDate(item.createdAt),
          width: 140,
        },
        {
          id: 'reasoning',
          header: 'Reasoning',
          cell: (item) => item.agentReasoning,
        },
        {
          id: 'actions',
          header: 'Actions',
          cell: (item) =>
            item.validatedActions.map((a) => a.type).join(', ') || '—',
          width: 220,
        },
      ]}
      items={items}
    />
  )
}
